import React, { useEffect } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from "@/components/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { getContrastTextColor } from "@/utils/colorUtils";
import ScrollRestoration from "@/components/ScrollRestoration";
import SitePopupHost from "@/components/customer/SitePopupHost";

// Common components
import Header from "../header";
import Footer from "../footer";

// Oripa-specific pages
import OripaTopPage from "./top";
import EventDetailsPage from "../events/details";

// Shared auth pages
import LoginPage from "../shared/auth/login";
import SignupPage from "../shared/auth/signup";
import ResetPasswordPage from "../shared/auth/reset-password";
import NewPasswordPage from "../shared/auth/new-password";
import VerifyEmailPage from "../shared/auth/verify-email";
import ConfirmEmailChangePage from "../shared/auth/confirm-email-change";
import VerifyPhonePage from "../shared/auth/verify-phone";
import EmailVerificationPendingPage from "../shared/auth/email-verification-pending";

// Oripa-specific user pages
import OripaPointPage from "./point";
import ProfilePage from "../profile";
import HomeAddressPage from "../home-address";
import NotificationsPage from "../notifications";
import ResultsPage from "../results";
import SettingsPage from "../settings";

// Oripa-specific products pages
import OripaProductsPage from "./products";
import OripaProductsConfirmPage from "./products-confirm";

// Other pages
import ContactPage from "../contact";
import NewsPage from "../news";
import NewsDetailPage from "../news-detail";
import LegalDocumentPage from "../legal-document";
import ClosedStorePage from "../closed-store";

interface ThemeConfig {
  nav_color?: string;
  primary_color?: string;
  secondary_color?: string;
  bg_color?: string;
  title_font?: string;
  title_size?: "small" | "medium" | "large" | "xlarge";
  body_font?: string;
  logo_type?: "text" | "image";
  logo_text?: string | null;
  logo_image_url?: string | null;
  design_style?: "neobrutalism" | "modern";
}

interface Site {
  id: number;
  name: string;
  subdomain: string;
  slug: string;
  description: string;
  icon_url: string | null;
  thumbnail_url: string | null;
  privacy_url: string | null;
  terms_url: string | null;
  trade_law_url: string | null;
  faq_url: string | null;
  created_at: string;
  identity_verified: boolean;
  antique_dealer_verified: boolean;
  delivery_count: number;
  require_phone_verification?: boolean;
  require_birth_date_verification?: boolean;
  point_purchase_enabled?: boolean;
  new_customer_registration_enabled?: boolean;
  theme_config?: ThemeConfig;
  site_type?: string;
  show_faq?: boolean;
  payment_provider?: 'fincode' | 'gmo_pg';
  gmo_pg_shop_id?: string;
  test_mode?: boolean;
  accepted_payment_methods?: string[];
  closed?: boolean;
  closed_at?: string | null;
  closure_title?: string;
  closure_message?: string;
  closure_contact_email?: string | null;
}

interface AppProps {
  siteData?: Site;
}

const OripaApp: React.FC<AppProps> = ({ siteData }) => {
  return (
    <AuthProvider>
      <HelmetProvider>
        <Router>
          <AppRoutes siteData={siteData} />
        </Router>
      </HelmetProvider>
    </AuthProvider>
  );
};

interface AppRoutesProps {
  siteData?: Site;
}

const RequireCustomer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, authLoading } = useAuth();
  const location = useLocation();

  if (authLoading) {
    return <div className="min-h-[40vh]" aria-busy="true" />;
  }

  if (!currentUser) {
    const redirect = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC<AppRoutesProps> = ({ siteData }) => {
  const location = useLocation();

  // フッターを非表示にするパス
  const hideFooterPaths = [/^\/oripa\/[a-zA-Z0-9-]+$/];

  // 現在のパスが非表示対象か判定
  const shouldHideFooter = hideFooterPaths.some((regex) => regex.test(location.pathname));

  // テーマ設定を適用
  useEffect(() => {
    const theme = siteData?.theme_config;
    if (theme) {
      if (theme.primary_color) {
        document.documentElement.style.setProperty("--primary-color", theme.primary_color);
        const buttonTextColor = getContrastTextColor(theme.primary_color);
        document.documentElement.style.setProperty("--button-text-color", buttonTextColor);
      }
      if (theme.secondary_color) {
        document.documentElement.style.setProperty("--secondary-color", theme.secondary_color);
        const secondaryButtonTextColor = getContrastTextColor(theme.secondary_color);
        document.documentElement.style.setProperty("--secondary-button-text-color", secondaryButtonTextColor);
      }

      if (theme.bg_color) {
        document.documentElement.style.setProperty("--bg-color", theme.bg_color);
        document.body.style.backgroundColor = theme.bg_color;
        const bgTextColor = getContrastTextColor(theme.bg_color);
        document.documentElement.style.setProperty("--bg-text-color", bgTextColor);
      }

      if (theme.nav_color) {
        document.documentElement.style.setProperty("--nav-color", theme.nav_color);
      }

      document.documentElement.classList.remove("style-neobrutalism", "style-modern");
      if (theme.design_style) {
        document.documentElement.classList.add(`style-${theme.design_style}`);
      }
    }

    return () => {
      document.documentElement.style.removeProperty("--primary-color");
      document.documentElement.style.removeProperty("--secondary-color");
      document.documentElement.style.removeProperty("--button-text-color");
      document.documentElement.style.removeProperty("--secondary-button-text-color");
      document.documentElement.style.removeProperty("--bg-color");
      document.documentElement.style.removeProperty("--bg-text-color");
      document.documentElement.style.removeProperty("--nav-color");
      document.body.style.backgroundColor = "";
      document.documentElement.classList.remove("style-neobrutalism", "style-modern");
    };
  }, [siteData?.theme_config]);

  if (!siteData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">サイトが見つかりません</h1>
        </div>
      </div>
    );
  }

  if (siteData.closed) {
    return (
      <ClosedStorePage
        siteName={siteData.name}
        iconUrl={siteData.icon_url}
        title={siteData.closure_title}
        message={siteData.closure_message}
        contactEmail={siteData.closure_contact_email}
      />
    );
  }

  return (
    <>
      <ScrollRestoration />
      <SitePopupHost />
      <Header siteData={siteData} />
      <main>
        <Routes>
          {/* Oripa Top */}
          <Route path="/" element={<OripaTopPage siteId={siteData.id} requirePhoneVerification={siteData.require_phone_verification} />} />

          {/* Auth pages */}
          <Route path="/login" element={<LoginPage siteId={siteData.id} siteData={siteData} />} />
          <Route path="/signup" element={<SignupPage siteId={siteData.id} siteData={siteData} />} />
          <Route path="/reset-password" element={<ResetPasswordPage siteId={siteData.id} siteData={siteData} />} />
          <Route path="/new-password" element={<NewPasswordPage />} />
          <Route path="/verify_email/:token" element={<VerifyEmailPage />} />
          <Route path="/confirm_email_change/:token" element={<ConfirmEmailChangePage />} />
          <Route path="/verify-phone" element={<VerifyPhonePage siteData={siteData} />} />
          <Route path="/email-verification-pending" element={<EmailVerificationPendingPage siteData={siteData} />} />

          {/* Oripa Event Details */}
          <Route path="/oripa/:eventId" element={<EventDetailsPage siteId={siteData.id} />} />

          {/* User pages */}
          <Route path="/point" element={<RequireCustomer><OripaPointPage siteData={siteData} /></RequireCustomer>} />
          <Route path="/point/complete" element={<RequireCustomer><OripaPointPage siteData={siteData} /></RequireCustomer>} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/home-address" element={<HomeAddressPage />} />
          <Route path="/products" element={<OripaProductsPage />} />
          <Route path="/products/confirm" element={<OripaProductsConfirmPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/results" element={<ResultsPage siteData={siteData} />} />
          <Route path="/settings" element={<SettingsPage />} />

          {/* Other pages */}
          <Route path="/contact" element={<ContactPage contactUrl={siteData.contact_url} />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/news/:slug" element={<NewsDetailPage />} />

          {/* Legal documents */}
          <Route path="/terms" element={<LegalDocumentPage siteId={siteData.id} />} />
          <Route path="/privacy" element={<LegalDocumentPage siteId={siteData.id} />} />
          <Route path="/trade-law" element={<LegalDocumentPage siteId={siteData.id} />} />
        </Routes>
      </main>
      {!shouldHideFooter && <Footer siteData={siteData} />}
      <Toaster position="top-center" />
    </>
  );
};

export default OripaApp;
